import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Grid,
  Typography,
  Radio,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DeduplicationMatch } from './DeduplicationTable';
import { getNestedProperty } from '../utils';
import { ReferralData } from '../types';

interface DeduplicationRowProps {
  match: DeduplicationMatch;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  showSelectRadio?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  columns?: Array<{ key: string; label: string }>;
  parentData?: {
    firstName?: string;
    lastName?: string;
    dob?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    gender?: string;
  };
}

const DeduplicationRow: React.FC<DeduplicationRowProps> = ({
  match,
  showSelectRadio = false,
  isSelected = false,
  onSelect,
  columns = [],
  parentData,
}) => {
  const [dedupeRowOpen, setDedupeRowOpen] = useState(false);

  const displayColumns = columns;

  // Helper function to find matching phone number from parent data
  const getMatchingPhoneNumber = (): string | null => {
    // Check if phone is in exact matches
    if (!match.patientMatchBreakdown?.exactMatches?.includes('phone')) {
      return null;
    }

    // Get parent phone number
    const parentPhone = parentData?.phoneNumber;
    if (!parentPhone) {
      return null;
    }

    // Clean phone number for comparison (remove non-digits)
    const cleanParentPhone = parentPhone.replace(/\D/g, '');

    // Check against phone1, phone2, phone3, phone4 in match data
    const phoneFields = ['phone1', 'phone2', 'phone3', 'phone4'];

    for (const phoneField of phoneFields) {
      const phoneObj = (match as any)[phoneField];
      if (phoneObj?.number) {
        const cleanMatchPhone = phoneObj.number.replace(/\D/g, '');
        if (cleanParentPhone === cleanMatchPhone) {
          return phoneObj.number;
        }
      }
      // Also check rawNumber field
      if (phoneObj?.rawNumber) {
        const cleanMatchPhone = phoneObj.rawNumber.replace(/\D/g, '');
        if (cleanParentPhone === cleanMatchPhone) {
          return phoneObj.rawNumber;
        }
      }
    }

    return null;
  };

  // key will be from match data and value will be the corresponding key in patientMatchBreakdown to determine match type for coloring
  const patientMatchBreakdownKeyMapping: { [key: string]: string } = {
    firstName: 'firstName',
    lastName: 'lastName',
    dateOfBirth: 'dateOfBirth',
    phoneNumber: 'phone',
    'primaryEmail.email': 'email',
    address1: 'address',
    genderCode: 'gender',
  };

  // Helper function to get cell background color based on match type
  const getCellBgColor = (fieldName: string) => {
    // Skip color coding for non-matchable fields
    if (fieldName === 'ccPatientReferenceId' || fieldName === 'patientMatchingScore') {
      return 'transparent';
    }

    if (
      match.patientMatchBreakdown.exactMatches.includes(patientMatchBreakdownKeyMapping[fieldName])
    ) {
      return '#D4EDDA'; // Green for exact match
    }
    if (
      match.patientMatchBreakdown.partialMatches.includes(
        patientMatchBreakdownKeyMapping[fieldName]
      )
    ) {
      return '#FFF3CD'; // Yellow for partial match
    }
    // If field is in the match data but not in exact or partial matches, it's a no match
    return '#F8D7DA'; // Red for no match
  };

  // const hasExpandableData = match.caregiver;
  const hasExpandableData = false; // for now hide expandable caregiver data

  return (
    <>
      <TableRow>
        {false && (
          <TableCell>
            {hasExpandableData && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setDedupeRowOpen(!dedupeRowOpen)}
              >
                {dedupeRowOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
        )}
        {showSelectRadio && (
          <TableCell sx={{ textAlign: 'center' }}>
            <Radio
              checked={isSelected}
              onChange={() => onSelect?.()}
              value={match.ccPatientReferenceId}
              sx={{
                color: '#255E5E',
                '&.Mui-checked': {
                  color: '#255E5E',
                },
              }}
            />
          </TableCell>
        )}
        {displayColumns.map((column) => {
          let fieldValue = getNestedProperty(match, column.key);

          // Special handling for phoneNumber field
          if (column.key === 'phoneNumber') {
            // First, try to get the matching phone number from parent data
            const matchingPhone = getMatchingPhoneNumber();
            if (matchingPhone) {
              fieldValue = matchingPhone;
            } else if (!fieldValue) {
              // Fallback: try to get from phone1, phone2, phone3, phone4
              const phoneFields = ['phone1', 'phone2', 'phone3', 'phone4'];
              for (const phoneField of phoneFields) {
                const phoneObj = (match as any)[phoneField];
                if (phoneObj?.number && !phoneObj?.outOfService) {
                  fieldValue = phoneObj.number;
                  break;
                }
              }
            }
          }

          if (column.key === 'dateOfBirth') {
            fieldValue = fieldValue ? new Date(fieldValue).toLocaleDateString() : '-';
          }

          // Special handling for address field - combine address1, city, state (represents the stateCode received from CC) and zip
          let displayValue: string;
          if (column.key === 'patientMatchingScore') {
            displayValue = `${fieldValue}%`;
          } else if (column.key === 'address1') {
            const address1 = (match as any).address1 || '';
            const city = (match as any).city || '';
            const stateCode = (match as any).state || '';
            const zip = (match as any).zip || '';
            displayValue = [address1, city, stateCode, zip].filter(Boolean).join(', ') || '-';
          } else {
            displayValue = fieldValue || '-';
          }

          return (
            <TableCell key={column.key} sx={{ backgroundColor: getCellBgColor(column.key) }}>
              {displayValue}
            </TableCell>
          );
        })}
      </TableRow>
      {hasExpandableData && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={displayColumns.length + (showSelectRadio ? 2 : 1)}
          >
            <Collapse in={dedupeRowOpen} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Grid container spacing={1.25}>
                  {/* Caregiver Information */}
                  {match.caregiver && (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            fontSize: '12px',
                            color: (theme: any) => theme.palette.warning?.dark || '#ED6C02',
                            mt: 1,
                            mb: 0.5,
                          }}
                        >
                          Caregiver Details:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '12px',
                              color: (theme: any) => theme.palette.neutral7?.main,
                              minWidth: '90px',
                            }}
                          >
                            First Name:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {match.caregiver.firstName || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '12px',
                              color: (theme: any) => theme.palette.neutral7?.main,
                              minWidth: '90px',
                            }}
                          >
                            Last Name:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {match.caregiver.lastName || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '12px',
                              color: (theme: any) => theme.palette.neutral7?.main,
                              minWidth: '90px',
                            }}
                          >
                            Email:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {match.caregiver.email || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '12px',
                              color: (theme: any) => theme.palette.neutral7?.main,
                              minWidth: '90px',
                            }}
                          >
                            Phone Number:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {match.caregiver.phoneNumber || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      {match.caregiver.gender && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: '12px',
                                color: (theme: any) => theme.palette.neutral7?.main,
                                minWidth: '90px',
                              }}
                            >
                              Gender:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {match.caregiver.gender}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {match.caregiver.address && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: '12px',
                                color: (theme: any) => theme.palette.neutral7?.main,
                                minWidth: '90px',
                              }}
                            >
                              Address:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {match.caregiver.address}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {/* {match.caregiver.city && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px', color: (theme: any) => theme.palette.neutral7?.main, minWidth: '90px' }}>
                              City:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{match.caregiver.city}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {match.caregiver.state && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px', color: (theme: any) => theme.palette.neutral7?.main, minWidth: '90px' }}>
                              State:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{match.caregiver.state}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {match.caregiver.zipcode && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px', color: (theme: any) => theme.palette.neutral7?.main, minWidth: '90px' }}>
                              Zipcode:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{match.caregiver.zipcode}</Typography>
                          </Box>
                        </Grid>
                      )} */}
                    </>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default DeduplicationRow;
